import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';

const ICON_SRC = process.env.PUBLIC_URL + '/favicon-32x32.png';
const LEAF_PNG_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAABAtJREFUWEfFl1toHGUUx39ndhNba42tWFG0aqWgoHhBRS2+KFQfbFKTmcS2iH0oVqQQDF5qZmJHM5tIaRErRQwGpCA2mUl6SX1oQWhFXxTFQouKFSRN1SYFtTU22ct8dmY3l93snVDP0z585//9vvM/35lvhSCesZehJTcgahFIFCGBry6gcRbRRkhEfmafPRquneeQUM+wdMAtoq2AP4BvQB3D1w4z0HlyPljSANgaRvJL4JEKRH9C+ISk/xGDXb9XkJe1NAMA6B2PIeqLuUIqAVJTZIM4ik+RqINrn6oUZAYgbcXXwIM5IglgGDiDYgXCTQU2CUB2E49v4+D2C+WCZAPo5kuI7C6YrNTnIMcQtQpYDZKdn04cBrUBNxZYWjKyBRrbbyCijQBakUwfnw+RZD9S0wbqaSAXJKhaK67zQSmCuScwrG+B+0slAr/gq2eRSB3iBxutzNM/23BjbxfTygNg7gRpKwMgWDIBaiMTNUMsSO64dFVfnFMNX7UyENtVSG8ugP5GIxIZKBMgWJYixWYGnV6azedR0gPUzspPklJPMBjLc8Pmegf1r93IFbVnKgBIQ6hUM173ILq1GmE/sHBGQ/3K35N3cWTHeK5uvi4G3Tpd5LoVYFPjJNXD7Os6gW42IAyARKYXK2w8563yAAwrsKCxwioEy49D9CFcO47RboLmzNL4i8n48twZkb8ChvUqsL0KAFC04znd2JfG+8lk4HswMzKhNuPGgh6ZjgIWmI8i8lVVAHCeiejtDNnnaOm4B18F13rKiiO4zpOlAQy7FpLngMVVQSg68Zw3w1zd6kNoDn8r/mXp6DX09ASDKoz8FQgTzf2INFQFgDrLkrGbw40aO1YRUTNjWfPvpq/rRGkAw3wOZE91AMFpU2vwug+Fh9TNU4isCLV8Vc9AbKgMAPsqSIyA1FUJ0YvrbApzDfN9kC0ZnU24Tm9pgLR/7yC8XiXAMK5zSwagBWRvxvQt9DvTX9zCPRCS20sh+SNwXVUQcbWcA7HTNNl3oCV/qLwCQUaT2YQWvBfzfvuLcynW4DmHMF5eiLpyHAk1GnCdg+VZMLWq2WxHSaziKihewXN2hnm6eR6RxWhyL32dxysDCO3oeAHUe8CCskGU2oUXa033gfVb2srxq3HfvVg5QGiHtRIhaMy1JV5NaX1hL/3OugxA8IUdxXXum32A4k1Y6Khrt95KTdQAHgceAK7NP9TkMG7nUxmAP1GyB68zXZFMVAeQC7Z+6xIS0WWg6sCPgJaCmn9gcgy3e4z0aL8Ifj1u12fzD1CqKQzrNuB7FkWv52N74vIDBK8kWI/nbMxlnR8LSlbAbEPJd3jO0f8HQLc68JzgdRT8yc2Ky1OBFutO+pypUZwF8B9orEgwBkGVIgAAAABJRU5ErkJggg==';
const HIGHLIGHT_ICON_SRC = `data:image/svg+xml;utf8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 32 32">
    <defs>
      <filter id="tint" color-interpolation-filters="sRGB">
        <feColorMatrix type="matrix" values="
          0 0 0 0 0
          0 0 0 0.83 0
          0 0 0 0.6 0
          0 0 0 1 0" />
        <feComponentTransfer>
          <feFuncA type="gamma" amplitude="1" exponent="0.35" offset="0" />
        </feComponentTransfer>
        <feDropShadow dx="0" dy="0" stdDeviation="1.2" flood-color="#0f172a" flood-opacity="0.8" />
      </filter>
    </defs>
    <image width="32" height="32" href="data:image/png;base64,${LEAF_PNG_BASE64}" filter="url(#tint)" />
  </svg>
`)}`;
const DISPLAY_SIZE = 38;
const HIGHLIGHT_SIZE = 50;

const baseMapContainerStyle = {
  width: '100%',
  height: '420px',
};

const defaultCenter = {
  lat: 22.08,
  lng: -159.52,
};

const DEFAULT_CENTER_BY_ISLAND = {
  kauai: { lat: 22.08, lng: -159.52 },
  oahu: { lat: 21.48, lng: -157.96 },
  maui: { lat: 20.8, lng: -156.33 },
  big_island: { lat: 19.62, lng: -155.55 },
};

const FIT_ZOOM_OFFSET_BY_ISLAND = {
  big_island: -1,
};

const mapOptions = {
  mapId: process.env.REACT_APP_MAP_ID,
  disableDefaultUI: true,
  zoomControl: true,
};

const LIBRARIES = ['places'];

function centerFromBeaches(beaches, island) {
  const withCoords = beaches.filter((beach) => Number.isFinite(beach?.location?.lat) && Number.isFinite(beach?.location?.lng));
  if (!withCoords.length) return DEFAULT_CENTER_BY_ISLAND[island] || defaultCenter;

  const sum = withCoords.reduce((acc, beach) => ({
    lat: acc.lat + beach.location.lat,
    lng: acc.lng + beach.location.lng,
  }), { lat: 0, lng: 0 });

  return {
    lat: sum.lat / withCoords.length,
    lng: sum.lng / withCoords.length,
  };
}

export default function BeachConditionsMap({
  island,
  beaches = [],
  selectedSlug,
  onSelectBeach,
  onVisibleBeachesChange,
  rankBySlug = null,
  mapHeight = '420px',
}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapRef = useRef(null);
  const lastVisibleKeyRef = useRef('');
  const [isMapReady, setIsMapReady] = useState(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || '',
    libraries: LIBRARIES,
    mapIds: [process.env.REACT_APP_MAP_ID],
  });

  const mapContainerStyle = useMemo(() => {
    const height = typeof mapHeight === 'number' ? `${mapHeight}px` : (mapHeight || '420px');
    return {
      ...baseMapContainerStyle,
      height,
    };
  }, [mapHeight]);

  const markerIcon = useMemo(() => {
    if (!(isLoaded && window.google)) return undefined;
    return {
      url: ICON_SRC,
      scaledSize: new window.google.maps.Size(DISPLAY_SIZE, DISPLAY_SIZE),
      anchor: new window.google.maps.Point(DISPLAY_SIZE / 2, DISPLAY_SIZE),
      labelOrigin: new window.google.maps.Point(DISPLAY_SIZE / 2, DISPLAY_SIZE / 2),
    };
  }, [isLoaded]);

  const selectedMarkerIcon = useMemo(() => {
    if (!(isLoaded && window.google)) return undefined;
    return {
      url: HIGHLIGHT_ICON_SRC,
      scaledSize: new window.google.maps.Size(HIGHLIGHT_SIZE, HIGHLIGHT_SIZE),
      anchor: new window.google.maps.Point(HIGHLIGHT_SIZE / 2, HIGHLIGHT_SIZE),
      labelOrigin: new window.google.maps.Point(HIGHLIGHT_SIZE / 2, HIGHLIGHT_SIZE / 2),
    };
  }, [isLoaded]);

  const center = useMemo(() => centerFromBeaches(beaches, island), [beaches, island]);
  const beachBoundsKey = useMemo(
    () => beaches.map((beach) => beach.slug).join('|'),
    [beaches],
  );

  const handleMapLoad = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
    setIsMapReady(true);
  }, []);

  const handleIdle = useCallback(() => {
    if (!mapRef.current || typeof onVisibleBeachesChange !== 'function' || !window.google) return;
    const bounds = mapRef.current.getBounds();
    if (!bounds) return;

    const visibleSlugs = beaches
      .filter((beach) => Number.isFinite(beach?.location?.lat) && Number.isFinite(beach?.location?.lng))
      .filter((beach) => bounds.contains(new window.google.maps.LatLng(beach.location.lat, beach.location.lng)))
      .map((beach) => beach.slug);

    const key = visibleSlugs.join('|');
    if (key !== lastVisibleKeyRef.current) {
      lastVisibleKeyRef.current = key;
      onVisibleBeachesChange(visibleSlugs);
    }
  }, [beaches, onVisibleBeachesChange]);

  useEffect(() => {
    if (!isLoaded || !isMapReady || !window.google || !mapRef.current) return;
    const withCoords = beaches.filter(
      (beach) => Number.isFinite(beach?.location?.lat) && Number.isFinite(beach?.location?.lng),
    );
    if (!withCoords.length) return;

    if (withCoords.length === 1) {
      mapRef.current.setCenter({
        lat: withCoords[0].location.lat,
        lng: withCoords[0].location.lng,
      });
      mapRef.current.setZoom(12);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    withCoords.forEach((beach) => {
      bounds.extend(new window.google.maps.LatLng(beach.location.lat, beach.location.lng));
    });
    const zoomOffset = FIT_ZOOM_OFFSET_BY_ISLAND[island] || 0;
    if (zoomOffset !== 0) {
      let applied = false;
      const applyZoomOffset = () => {
        if (applied) return;
        applied = true;
        const currentZoom = mapRef.current?.getZoom?.();
        if (typeof currentZoom === 'number') {
          mapRef.current.setZoom(Math.max(1, currentZoom + zoomOffset));
        }
      };
      window.google.maps.event.addListenerOnce(mapRef.current, 'idle', applyZoomOffset);
      window.setTimeout(applyZoomOffset, 350);
    }
    mapRef.current.fitBounds(bounds, 48);
  }, [isLoaded, isMapReady, beachBoundsKey, beaches, island]);

  if (!apiKey) {
    return (
      <div className="rounded-lg border border-white/15 bg-white/5 px-4 py-6 text-sm text-white/70">
        Map unavailable: missing `REACT_APP_GOOGLE_MAPS_API_KEY`.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-lg border border-red-300/40 bg-red-900/20 px-4 py-6 text-sm text-red-200">
        Failed to load the map. Please use the beach list below.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="rounded-lg border border-white/15 bg-white/5 px-4 py-6 text-sm text-white/70">
        Loading map...
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-lg border border-white/15 bg-black/25">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={10}
        options={mapOptions}
        onLoad={handleMapLoad}
        onIdle={handleIdle}
      >
        {beaches
          .filter((beach) => Number.isFinite(beach?.location?.lat) && Number.isFinite(beach?.location?.lng))
          .map((beach) => {
            const isSelected = beach.slug === selectedSlug;
            const rank = rankBySlug?.get?.(beach.slug);
            return (
            <Marker
              key={beach.slug}
              position={{ lat: beach.location.lat, lng: beach.location.lng }}
              title={beach.name}
              icon={isSelected ? selectedMarkerIcon : markerIcon}
              label={rank ? {
                text: `#${rank}`,
                color: '#ffffff',
                fontSize: isSelected ? '14px' : '12px',
                fontWeight: '700',
              } : undefined}
              onClick={() => {
                if (typeof onSelectBeach === 'function') {
                  onSelectBeach(isSelected ? '' : beach.slug);
                }
              }}
              zIndex={isSelected ? 999 : undefined}
            />
            );
          })}
      </GoogleMap>
    </div>
  );
}
